import React, { useCallback, useEffect, useMemo, useState, memo, useRef } from 'react';
import { Position, useReactFlow } from '@xyflow/react';
import { AppNode, NodeType, NodeStatus, LightingParams } from '@/types';
import { useSelectionStore } from '@/stores/selectionStore';
import { useReferenceStore } from '@/stores/referenceStore';
import { useNodeStore } from '@/stores/nodeStore';
import { toast } from 'sonner';
import { NODE_TYPE_CONFIG } from '@/config/nodeTypeConfig';
import { NodeTitle } from './parts/NodeTitle';
import { TopFunctionBar, HD_FEATURE_LABEL } from './parts/TopFunctionBar';
import { UploadButton } from './parts/UploadButton';
import { TryActions } from './parts/TryActions';
import { EditorPanel } from './parts/EditorPanel';
import { MagneticHandle } from './parts/MagneticHandle';
import { InverseScaleBox } from './parts/InverseScaleBox';
import { MediaDimensions } from './parts/MediaDimensions';
import { FEATURE_GRIDS } from './parts/GridOverlay';
import { PanoramaPanel, PanoramaToolbar } from './parts/PanoramaPanel';
import { MultiAnglePanel } from './parts/MultiAnglePanel';
import { LightingPanel } from './parts/lighting/LightingPanel';
import { FeatureConfirmBar } from './parts/FeatureConfirmBar';
import { GridSplitBar } from './parts/GridSplitBar';
import { PanoramaContent, MediaUploadContent, AudioContent, GeneratorContent, SelfWriteContent, DirectorContent, LayerEditContent, ScriptContent, SmartEditContent } from './parts/CardContentRenderer';
import { ScriptNodeToolbar, ScriptQuickInput } from './parts/content/ScriptNodeToolbar';
import { SmartEditQuickInput } from './parts/content/SmartEditQuickInput';
import { generateBatchStoryboard, generateBatchVideos } from '@/components/ScriptPanel/useScriptBatchGenerator';
import { readScript, ScriptShotGenRef } from '@/types/scriptNode';
import { FloatingToolbar } from './parts/FloatingToolbar';
import { useInputAssets, useUpstreamVideo, useGenerateNodeActions, useNodeMedia, useNodeInteraction, useSelfWriteEditor } from './hooks';
import { TOPAZ_MODEL_ID, DEFAULT_UPSCALE_SUBMODEL, normalizeUpscaleResolution } from '@/services/generationStrategies';

// v0.33 智能剪辑连接素材态：非智能剪辑节点传空表（模块级常量保持 selector 引用稳定）
const EMPTY_INPUT_IDS: string[] = [];

const SHORT_EDGE = 420;
const DEFAULT_ASPECT_RATIO = '16:9';
const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";

const isUploadNode = (nodeType: NodeType) => nodeType === NodeType.MEDIA_UPLOAD;

interface GenerateNodeProps {
  data: AppNode;
  selected: boolean;
  dragging?: boolean;
  onNodeAction: (id: string, prompt?: string) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onNodeDelete: (id: string) => void;
  onExtractImage?: (nodeId: string, image: string) => void;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  onEnterPanorama?: (nodeId: string, imageUrl: string) => void;
  onGeneratePanorama?: (sourceNodeId: string, params: {
    prompt: string;
    quality: import('@/types').PanoramaQuality;
    referenceImageUrl?: string;
    mode: 'txt2panorama' | 'img2panorama';
    model?: string;
  }) => void;
  onEnterMultiAngle?: (nodeId: string, imageUrl: string) => void;
  onGenerateMultiAngle?: (sourceNodeId: string, params: {
    prompt: string;
    imageUrl: string;
    model?: string;
    aspectRatio?: string;
    multiAngleParams: import('@/types').MultiAngleParams;
  }) => void;
  onGenerateLighting?: (sourceNodeId: string, params: {
    imageUrl: string;
    model?: string;
    lightingParams: LightingParams;
    lightingRequest: { lighting_style: string };
  }) => void;
  onToggleChat?: () => void;
  onOpenChat?: () => void;
  onOpenDirector?: (nodeId: string) => void;
  onOpenLayerEdit?: (nodeId: string) => void;
  onOpenScript?: (nodeId: string) => void;
  /** 智能剪辑：携带输入栏会话上下文打开全屏面板（§4.1.3） */
  onOpenSmartEdit?: (nodeId: string, session?: import('@/types/smartEdit').SmartEditSession | null) => void;
}

const GenerateNodeComponent: React.FC<GenerateNodeProps> = memo(({
  data,
  selected,
  dragging = false,
  onNodeAction,
  onNodeUpdate,
  onNodeDelete,
  onExtractImage,
  onAddNode,
  onEnterPanorama,
  onGeneratePanorama,
  onEnterMultiAngle,
  onGenerateMultiAngle,
  onGenerateLighting,
  onToggleChat,
  onOpenChat,
  onOpenDirector,
  onOpenLayerEdit,
  onOpenScript,
  onOpenSmartEdit,
}) => {
  const config = NODE_TYPE_CONFIG[data.type] || NODE_TYPE_CONFIG[NodeType.IMAGE_GENERATOR];
  const IconComponent = config.icon;
  const isWorking = data.status === NodeStatus.WORKING;
  const isQueued = data.status === NodeStatus.QUEUED;
  const hasError = data.status === NodeStatus.ERROR;

  const [localPrompt, setLocalPrompt] = useState(data.data.prompt || '');
  // v1.4.16：脚本节点底部补充输入草稿提升至此（镜像 localPrompt 模式）——ScriptQuickInput 随取消选中卸载后草稿不丢
  const [scriptQuickText, setScriptQuickText] = useState('');
  // v0.32：智能剪辑底部参数面板草稿（镜像 scriptQuickText）——取消选中卸载后输入不丢
  const [smartQuickText, setSmartQuickText] = useState('');

  const {
    onNodeUpdateRef,
    commitPrompt,
    handleGenerate,
    handlePromptChange,
    handlePromptSubmit,
    handleImageModeSwitch,
    handleAddUploadNodeForPanorama,
    handleTryAction,
    currentImageMode,
  } = useGenerateNodeActions({
    data,
    localPrompt,
    onNodeAction,
    onNodeUpdate,
    onAddNode: useCallback((node: AppNode, position: { x: number; y: number }, options?: { connectToNodeId?: string }) => {
      useNodeStore.getState().addNode(node);
      if (options?.connectToNodeId) {
        useNodeStore.getState().addConnection(node.id, options.connectToNodeId);
      }
    }, [onAddNode]),
    onSetLocalPrompt: setLocalPrompt,
  });

  const {
    isHovered,
    setIsHovered,
    isInputFocused,
    isCtrlHeld,
    activeFeature,
    gridSplit,
    showPanoramaPanel,
    showMultiAnglePanel,
    showLightingPanel,
    chargeComplete,
    selectedCells,
    setSelectedCells,
    handleFeatureSelect,
    handlePanoramaClick,
    handleGridSplitClose,
    handleFeatureClose,
    handlePanoramaClose,
    handleMultiAngleClick,
    handleMultiAngleClose,
    handleLightingClick,
    handleLightingClose,
  } = useNodeInteraction({ data });

  const handleHdDerive = useCallback(() => {
    if (!onAddNode) return;
    const newX = data.x + 500;
    const newY = data.y;
    const hdResolution = normalizeUpscaleResolution(data.data.resolution);
    const newNodeId = onAddNode(NodeType.IMAGE_GENERATOR, { x: newX, y: newY }, {
      model: TOPAZ_MODEL_ID,
      resolution: hdResolution,
      upscaleSubModel: data.data.upscaleSubModel || DEFAULT_UPSCALE_SUBMODEL,
      prompt: '',
      generationMode: 'UPSCALE',
      aspectRatio: data.data.aspectRatio,
    });
    if (!newNodeId) return;
    useNodeStore.getState().addConnection(data.id, newNodeId);
    onNodeUpdate(newNodeId, {}, undefined, HD_FEATURE_LABEL);
    useSelectionStore.getState().setSelectedNodeIds([newNodeId]);
  }, [data.x, data.y, data.data.resolution, data.data.upscaleSubModel, data.data.aspectRatio, onAddNode, onNodeUpdate]);

  const handleFeatureSelectWrapped = useCallback((featureId: string, featureLabel: string) => {
    if (featureLabel === HD_FEATURE_LABEL) {
      handleHdDerive();
      return;
    }
    handleFeatureSelect(featureId, featureLabel);
  }, [handleFeatureSelect, handleHdDerive]);

  const {
    isPlayingAudio,
    setIsPlayingAudio,
    isPlayingVideo,
    videoProgress,
    videoDuration,
    videoVolume,
    isVideoMuted,
    hasAudioTrack,
    showImageGrid,
    mediaRef,
    handleMouseEnter,
    handleMouseLeave,
    toggleVideoPlay,
    toggleVideoMute,
    changeVideoVolume,
    handleVideoTimeUpdate,
    handleVideoSeek,
    handleVideoEnded,
    handleVideoLoadedMetadata,
  } = useNodeMedia({ data, shouldPlay: isHovered || selected });

  const selfWriteResult = useSelfWriteEditor({
    data,
    onNodeUpdate,
  });

  // P1-4: 改为订阅布尔值，避免每次选中数量变化都重渲染所有 GenerateNode
  // 之前订阅 selectedNodeIds.size（数字），任何选中变化都触发重渲染
  // 现在只有"是否多选"状态变化（0/1 → 2+ 或 2+ → 0/1）才触发重渲染
  const isMultiSelectedState = useSelectionStore((s) => s.selectedNodeIds.size > 1);
  const isBoxSelecting = useSelectionStore((s) => s.isBoxSelecting);
  const isMultiSelected = selected && (isMultiSelectedState || isBoxSelecting);

  useEffect(() => { setLocalPrompt(data.data.prompt || ''); }, [data.data.prompt]);

  // 自动获取媒体尺寸 — AI 生成图片/视频不返回尺寸，旧节点也缺少尺寸字段
  // 在前端异步加载图片/视频获取自然尺寸并回写，使 MediaDimensions 能正常显示
  // 关键：此处直接写 store，绕过 onNodeUpdate(handleNodeUpdateWithHistory)。
  // 尺寸探测是自动副作用（非用户编辑），不应记入撤销历史；否则 undo 恢复旧快照后
  // 异步 onload 回调会触发 saveHistory，把当前状态重新推入历史，使连续撤销失效。
  useEffect(() => {
    const imageData = data.data;
    // 图片：有 image 但缺少 imageDimensions
    const imageUrl = imageData.image || (imageData.images && imageData.images[0]) || imageData.panoramaOutputUrl;
    if (imageUrl && (!imageData.imageDimensions || !imageData.imageDimensions.width)) {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          useNodeStore.getState().updateNode(data.id, { imageDimensions: { width: img.naturalWidth, height: img.naturalHeight } });
        }
      };
      img.src = imageUrl;
      return;
    }
    // 视频：有 videoUri 但 videoMetadata 缺少 width/height
    const videoUrl = imageData.videoUri || (imageData.videoUris && imageData.videoUris[0]);
    if (videoUrl && (!imageData.videoMetadata || !imageData.videoMetadata.width || !imageData.videoMetadata.height)) {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        if (video.videoWidth && video.videoHeight) {
          useNodeStore.getState().updateNode(data.id, {
            videoMetadata: {
              width: video.videoWidth,
              height: video.videoHeight,
              duration: video.duration,
            },
          });
        }
      };
      video.src = videoUrl;
    }
  }, [data.id, data.data.image, data.data.images, data.data.videoUri, data.data.videoUris, data.data.panoramaOutputUrl, data.data.imageDimensions, data.data.videoMetadata]);

  // 文本节点自己编写模式
  const isSelfWrite = data.type === NodeType.PROMPT_INPUT && data.data.subMode === 'self-write';

  // v1.4.88 分镜生图：传入 scriptShotRef，参考图预览行按锚定表展开脚本资产图（与生成请求同源同序）
  const inputAssets = useInputAssets(data.inputs, (data.data as { scriptShotRef?: ScriptShotGenRef }).scriptShotRef);

  const isDirector = data.type === NodeType.DIRECTOR;
  const isLayerEdit = data.type === NodeType.LAYER_EDIT;
  const isScript = data.type === NodeType.SCRIPT_NODE;
  const isSmartEdit = data.type === NodeType.VIDEO_COMPOSE;
  // v0.33 连接素材态：首个上游视频（封面 + 真实时长），卡片与底部面板共用
  const upstreamVideo = useUpstreamVideo(isSmartEdit ? data.inputs : EMPTY_INPUT_IDS);
  const DIRECTOR_WIDTH = 728;
  const DIRECTOR_HEIGHT = 720;
  // 图层编辑卡片默认与导演台同规格（v0.30 实核定案，方案 4.1.2）
  const LAYER_EDIT_WIDTH = 728;
  const LAYER_EDIT_HEIGHT = 720;
  // 智能剪辑卡片与脚本节点同规格长方形（v0.32 用户反馈：参考脚本节点样式；已保存仍按剪辑画布比例变形）
  const SMART_EDIT_WIDTH = SHORT_EDGE * 16 / 9;
  const SMART_EDIT_HEIGHT = SHORT_EDGE;
  // 脚本节点卡片固定规格（v1.4.10：与图片节点同构的横向长方形——短边 420、默认 16:9 比例；空态/生成中/完成态统一）
  const SCRIPT_WIDTH = SHORT_EDGE * 16 / 9;
  const SCRIPT_HEIGHT = SHORT_EDGE;
  // 脚本节点完成态（方案 §1.1）：手动编写空脚本也算（可直接加镜头）；生成来源需已产出镜头
  const scriptData = isScript ? readScript((data.data as { script?: unknown }).script) : undefined;
  const scriptDone = !!scriptData && (scriptData.source === 'MANUAL' || scriptData.shots.length > 0);

  const { nodeWidth, cardHeight } = useMemo(() => {
    if (isSelfWrite) return { nodeWidth: data.data.nodeSize?.width || 840, cardHeight: data.data.nodeSize?.height || 472 };
    if (isDirector) return { nodeWidth: DIRECTOR_WIDTH, cardHeight: DIRECTOR_HEIGHT };
    if (isScript) return { nodeWidth: SCRIPT_WIDTH, cardHeight: SCRIPT_HEIGHT };
    if (isLayerEdit) {
      // 已保存：卡片按图层文档画布比例变形，缩略图全出血铺满（长边不超默认规格包络）
      const layerDoc = (data.data as any)?.layerDocument;
      const canvasW = Number(layerDoc?.canvasWidth) || 0;
      const canvasH = Number(layerDoc?.canvasHeight) || 0;
      if ((data.data as any)?.thumbnailDataUrl && canvasW > 0 && canvasH > 0) {
        const ratio = canvasW / canvasH;
        if (ratio >= 1) return { nodeWidth: LAYER_EDIT_WIDTH, cardHeight: Math.round(LAYER_EDIT_WIDTH / ratio) };
        return { nodeWidth: Math.round(LAYER_EDIT_HEIGHT * ratio), cardHeight: LAYER_EDIT_HEIGHT };
      }
      return { nodeWidth: LAYER_EDIT_WIDTH, cardHeight: LAYER_EDIT_HEIGHT };
    }
    if (isSmartEdit) {
      // 已保存：卡片按剪辑画布比例变形，封面全出血铺满（方案 §4.1.2）
      const editDoc = (data.data as any)?.editDocument as { canvas?: { width?: number; height?: number } } | undefined;
      const canvasW = Number(editDoc?.canvas?.width) || 0;
      const canvasH = Number(editDoc?.canvas?.height) || 0;
      if ((data.data as any)?.thumbnailDataUrl && canvasW > 0 && canvasH > 0) {
        const ratio = canvasW / canvasH;
        if (ratio >= 1) return { nodeWidth: SMART_EDIT_WIDTH, cardHeight: Math.round(SMART_EDIT_WIDTH / ratio) };
        return { nodeWidth: Math.round(SMART_EDIT_HEIGHT * ratio), cardHeight: SMART_EDIT_HEIGHT };
      }
      return { nodeWidth: SMART_EDIT_WIDTH, cardHeight: SMART_EDIT_HEIGHT };
    }
    if (data.height) return { nodeWidth: data.width || SHORT_EDGE, cardHeight: data.height };

    const ratio = data.data.aspectRatio || DEFAULT_ASPECT_RATIO;
    const [w, h] = ratio.split(':').map(Number);
    const isLandscape = w >= h;

    // 短边固定 420，长边按比例计算
    const width = isLandscape ? SHORT_EDGE * w / h : SHORT_EDGE;
    const height = isLandscape ? SHORT_EDGE : SHORT_EDGE * h / w;

    return { nodeWidth: width, cardHeight: height };
  }, [data.height, data.width, data.data.aspectRatio, data.data.nodeSize, isSelfWrite, isDirector, isLayerEdit, isScript, isSmartEdit, (data.data as any)?.layerDocument, (data.data as any)?.editDocument, (data.data as any)?.thumbnailDataUrl]);

  // 打灯/多角度面板自动聚焦（方案 §3 交互要点 6）：面板展开时画布平滑适配居中「节点 + 面板」联合区域
  // 三个易错点：① 不可用 fitView({nodes})——面板是节点内 absolute 子元素，不在节点注册 bbox 内；
  // ② setViewport 的 x/y 是平移量而非中心坐标；③ 计算顺序：先定 zoomTarget → 再算联合包围盒 → 最后算平移量
  // 流畅性：不用 setViewport 自带 d3 transition（与面板挂载同帧抢主线程、起步缓动陡导致卡顿感），
  // 改为双 rAF 等面板首绘后，自绘 easeOutCubic 逐帧插值；用户滚轮/按下立即打断
  const reactFlowInstance = useReactFlow();
  const viewportAnimRef = useRef<number | null>(null);
  useEffect(() => {
    if (!showLightingPanel && !showMultiAnglePanel) return;
    const PANEL_W = showMultiAnglePanel ? 896 : 1120; // 固定视觉尺寸：多角度 896 / 打灯 1120
    const PANEL_H = 504;
    const MARGIN = 0.9;
    const flowEl = document.querySelector<HTMLElement>('.react-flow');
    const availW = ((flowEl?.clientWidth ?? window.innerWidth) * MARGIN);
    const availH = ((flowEl?.clientHeight ?? window.innerHeight) * MARGIN);
    const zoomNow = reactFlowInstance.getViewport().zoom;
    const totalH = cardHeight + 8 + PANEL_H;
    // zoomTarget：面板（InverseScaleBox 固定视觉尺寸）完整可见，clamp 到项目 zoom 范围
    const zoomTarget = Math.max(0.1, Math.min(5, zoomNow, availW / (PANEL_W + 80), availH / (totalH + 80)));
    // 联合包围盒（画布坐标，面板画布占位 = 固定视觉尺寸 / zoom）
    const panelW = PANEL_W / zoomTarget;
    const panelH = PANEL_H / zoomTarget;
    const nodeCenterX = data.x + nodeWidth / 2;
    const minX = Math.min(data.x, nodeCenterX - panelW / 2);
    const maxX = Math.max(data.x + nodeWidth, nodeCenterX + panelW / 2);
    const minY = data.y;
    const maxY = data.y + cardHeight + 8 / zoomTarget + panelH;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    // 置中换算（x/y 为平移量语义）
    const W = flowEl?.clientWidth ?? window.innerWidth;
    const H = flowEl?.clientHeight ?? window.innerHeight;
    const to = { x: W / 2 - centerX * zoomTarget, y: H / 2 - centerY * zoomTarget, zoom: zoomTarget };
    const from = reactFlowInstance.getViewport();
    // 目标与当前视口几乎一致时跳过动画，避免无谓的逐帧 store 更新
    if (Math.abs(from.x - to.x) < 0.5 && Math.abs(from.y - to.y) < 0.5 && Math.abs(from.zoom - to.zoom) < 0.001) return;
    const stopAnim = () => {
      if (viewportAnimRef.current !== null) {
        cancelAnimationFrame(viewportAnimRef.current);
        viewportAnimRef.current = null;
      }
      flowEl?.removeEventListener('wheel', stopAnim);
      flowEl?.removeEventListener('pointerdown', stopAnim);
    };
    // 用户手势优先：动画中滚轮/按下立即让位，避免动画与手势拉扯
    flowEl?.addEventListener('wheel', stopAnim, { passive: true });
    flowEl?.addEventListener('pointerdown', stopAnim);
    const DURATION = 400;
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    let start: number | null = null;
    const step = (now: number) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / DURATION);
      const k = easeOutCubic(t);
      reactFlowInstance.setViewport({
        x: from.x + (to.x - from.x) * k,
        y: from.y + (to.y - from.y) * k,
        zoom: from.zoom + (to.zoom - from.zoom) * k,
      });
      if (t < 1) {
        viewportAnimRef.current = requestAnimationFrame(step);
      } else {
        stopAnim();
      }
    };
    // 双 rAF：先让面板完成挂载首绘，再起动画，避免开局两帧与面板布局抢主线程掉帧
    viewportAnimRef.current = requestAnimationFrame(() => {
      viewportAnimRef.current = requestAnimationFrame(step);
    });
    return stopAnim;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLightingPanel, showMultiAnglePanel]);

  const hasMedia = !!(
    data.data.image ||
    (data.data.images && data.data.images.length > 0) ||
    data.data.videoUri ||
    (data.data.videoUris && data.data.videoUris.length > 0) ||
    data.data.audioUri ||
    data.data.panoramaOutputUrl
  );
  const isOpen = isHovered || isInputFocused || selected;
  const uploadNode = isUploadNode(data.type);
  const mediaType = data.data.image || (data.data.images && data.data.images.length > 0)
    ? 'image'
    : data.data.videoUri || (data.data.videoUris && data.data.videoUris.length > 0)
      ? 'video'
      : data.data.audioUri
        ? 'audio'
        : data.data.panoramaOutputUrl
          ? 'image'
          : undefined;
  const mediaUrl = data.data.image ||
    (data.data.images && data.data.images[0]) ||
    data.data.videoUri ||
    (data.data.videoUris && data.data.videoUris[0]) ||
    data.data.audioUri ||
    data.data.panoramaOutputUrl;
  const activeFeatureGrid = activeFeature ? FEATURE_GRIDS[activeFeature.label] : null;

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
    handleMouseEnter();
  }, [handleMouseEnter, setIsHovered]);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
    handleMouseLeave();
  }, [handleMouseLeave, setIsHovered]);

  const renderCardContent = () => {
    if (isSelfWrite) {
      return (
        <SelfWriteContent
          data={data}
          selected={selected}
          editMode={selfWriteResult.editMode}
          editor={selfWriteResult.editor}
          onEnterEdit={selfWriteResult.enterEdit}
          onEnterFullscreen={selfWriteResult.enterFullscreen}
          onExitToPreview={selfWriteResult.exitToPreview}
          onExitToEdit={selfWriteResult.exitToEdit}
          onResize={selfWriteResult.handleResize}
        />
      );
    }
    if (data.type === NodeType.MEDIA_UPLOAD) {
      return (
        <MediaUploadContent
          data={data}
          hasMedia={hasMedia}
          gridSplit={gridSplit}
          activeFeatureGrid={activeFeatureGrid}
          selectedCells={selectedCells}
          onSelectedCellsChange={setSelectedCells}
          mediaRef={mediaRef}
          isHovered={isHovered}
          selected={selected}
          isPlayingAudio={isPlayingAudio}
          setIsPlayingAudio={setIsPlayingAudio}
          isPlayingVideo={isPlayingVideo}
          videoProgress={videoProgress}
          videoDuration={videoDuration}
          videoVolume={videoVolume}
          isVideoMuted={isVideoMuted}
          hasAudioTrack={hasAudioTrack}
          onToggleVideoPlay={toggleVideoPlay}
          onToggleVideoMute={toggleVideoMute}
          onChangeVideoVolume={changeVideoVolume}
          onVideoTimeUpdate={handleVideoTimeUpdate}
          onVideoSeek={handleVideoSeek}
          onVideoEnded={handleVideoEnded}
          onVideoLoadedMetadata={handleVideoLoadedMetadata}
          onExtractImage={onExtractImage}
        />
      );
    }
    if (data.type === NodeType.AUDIO_GENERATOR) {
      return (
        <AudioContent
          data={data}
          isWorking={isWorking}
          hasError={hasError}
          mediaRef={mediaRef}
          isPlayingAudio={isPlayingAudio}
          setIsPlayingAudio={setIsPlayingAudio}
        />
      );
    }
    if (data.type === NodeType.PANORAMA_GENERATOR) {
      return (
        <PanoramaContent
          data={data}
          isWorking={isWorking}
          hasError={hasError}
          onNodeUpdate={onNodeUpdate}
        />
      );
    }
    if (data.type === NodeType.DIRECTOR) {
      return (
        <DirectorContent
          data={data}
          onOpenDirector={onOpenDirector}
        />
      );
    }
    if (data.type === NodeType.LAYER_EDIT) {
      return (
        <LayerEditContent
          data={data}
          onOpenLayerEdit={onOpenLayerEdit}
        />
      );
    }
    if (data.type === NodeType.SCRIPT_NODE) {
      return (
        <ScriptContent
          data={data}
          isWorking={isWorking}
          onOpenScript={onOpenScript}
        />
      );
    }
    if (data.type === NodeType.VIDEO_COMPOSE) {
      return (
        <SmartEditContent
          data={data}
          onOpenSmartEdit={onOpenSmartEdit}
          upstreamVideo={upstreamVideo}
        />
      );
    }
    return (
      <GeneratorContent
        data={data}
        isWorking={isWorking}
        hasError={hasError}
        hasMedia={hasMedia}
        showImageGrid={showImageGrid}
        gridSplit={gridSplit}
        activeFeatureGrid={activeFeatureGrid}
        selectedCells={selectedCells}
        onSelectedCellsChange={setSelectedCells}
        onNodeUpdate={onNodeUpdate}
        mediaRef={mediaRef}
        IconComponent={IconComponent}
        isHovered={isHovered}
        selected={selected}
        isPlayingVideo={isPlayingVideo}
        videoProgress={videoProgress}
        videoDuration={videoDuration}
        videoVolume={videoVolume}
        isVideoMuted={isVideoMuted}
        hasAudioTrack={hasAudioTrack}
        onToggleVideoPlay={toggleVideoPlay}
        onToggleVideoMute={toggleVideoMute}
        onChangeVideoVolume={changeVideoVolume}
        onVideoTimeUpdate={handleVideoTimeUpdate}
        onVideoSeek={handleVideoSeek}
        onVideoEnded={handleVideoEnded}
        onVideoLoadedMetadata={handleVideoLoadedMetadata}
        onExtractImage={onExtractImage}
      />
    );
  };

  return (
    <div
      className="relative"
      style={{
        width: nodeWidth,
        height: cardHeight,
        // 强制根 div 尺寸等于视觉卡片，避免 ResizeObserver 测量被浮动 UI 撑大导致框选误命中
        contain: 'size',
        transformOrigin: 'center center',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {isSelfWrite && selfWriteResult.editMode === 'edit' && selfWriteResult.editor && (
        <InverseScaleBox
          minZoom={0.5}
          fixedContent
          fitViewport
          className="absolute left-1/2 flex justify-center z-30"
          style={{ bottom: '100%', paddingBottom: '36px', transform: 'translateX(-50%)', transformOrigin: 'center bottom' }}
          scaleOrigin="center bottom"
        >
          <FloatingToolbar
            editor={selfWriteResult.editor}
            onFullscreen={selfWriteResult.enterFullscreen}
            showWordCount
          />
        </InverseScaleBox>
      )}

      {selected && !dragging && !isMultiSelected && !activeFeature && !gridSplit && !showPanoramaPanel && !showMultiAnglePanel && !showLightingPanel && !isSelfWrite && !isDirector && !isLayerEdit && !isScript && (
        <InverseScaleBox
          minZoom={0.5}
          fixedContent
          fitViewport
          className="absolute left-1/2 flex justify-center z-40"
          style={{ bottom: '100%', paddingBottom: '36px', transform: 'translateX(-50%)', transformOrigin: 'center bottom' }}
          scaleOrigin="center bottom"
        >
          {data.type === NodeType.PANORAMA_GENERATOR && data.data.isPanoramaPreviewOpen && data.data.panoramaOutputUrl ? (
            <PanoramaToolbar data={data} onNodeUpdate={onNodeUpdate} />
          ) : hasMedia ? (
            <TopFunctionBar
              nodeId={data.id}
              nodeType={data.type}
              status={data.status}
              hasMedia={hasMedia}
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              images={data.data.images}
              hasInputAssets={inputAssets.length > 0}
              onUpdate={onNodeUpdate}
              onExtractImage={onExtractImage}
              onDelete={onNodeDelete}
              onFeatureSelect={handleFeatureSelectWrapped}
              onEnterPanorama={handlePanoramaClick}
              onEnterMultiAngle={handleMultiAngleClick}
              onEnterLighting={handleLightingClick}
            />
          ) : (
            <UploadButton
              nodeId={data.id}
              nodeType={data.type}
              onUpdate={onNodeUpdate}
            />
          )}
        </InverseScaleBox>
      )}

      {selected && !dragging && !isMultiSelected && showPanoramaPanel && (
        <PanoramaPanel
          data={data}
          cardHeight={cardHeight}
          onNodeUpdate={onNodeUpdate}
          onGeneratePanorama={onGeneratePanorama}
          onClose={handlePanoramaClose}
        />
      )}

      {selected && !dragging && !isMultiSelected && showMultiAnglePanel && (
        <MultiAnglePanel
          data={data}
          cardHeight={cardHeight}
          onNodeUpdate={onNodeUpdate}
          onGenerateMultiAngle={onGenerateMultiAngle}
          onClose={handleMultiAngleClose}
        />
      )}

      {selected && !dragging && !isMultiSelected && showLightingPanel && (
        <LightingPanel
          data={data}
          cardHeight={cardHeight}
          onNodeUpdate={onNodeUpdate}
          onGenerateLighting={onGenerateLighting}
          onClose={handleLightingClose}
        />
      )}

      {selected && !dragging && !isMultiSelected && gridSplit && (
        <GridSplitBar
          data={data}
          cardHeight={cardHeight}
          gridSplit={gridSplit}
          selectedCells={selectedCells}
          onAddNode={onAddNode}
          onClose={handleGridSplitClose}
        />
      )}

      {/* 节点标题 — 用 InverseScaleBox 包裹，画布缩放时保持视觉尺寸和位置不变
          zoom <= 0.7 时标题跟随画布缩放（字体变小），padding 也按比例缩小 */}
      <InverseScaleBox
        minZoom={0.5}
        className="absolute left-0 z-10"
        style={{ bottom: '100%', paddingBottom: '4px', width: '210px' }}
        scaleOrigin="bottom left"
      >
        <NodeTitle
          nodeId={data.id}
          title={data.title}
          type={data.type}
          onUpdate={onNodeUpdate}
        />
      </InverseScaleBox>

      {/* 脚本节点完成态顶部工具条（方案 §1.1 / 参考图「生成后的节点状态.png」）：
          v1.4.13 对齐图片节点 TopFunctionBar——仅选中态挂载（selected && !dragging && !isMultiSelected）、
          槽位 paddingBottom 36px 与图片节点顶栏一致；底部补充输入栏已升级为选中态底部参数面板（v1.4.9，与图片节点 EditorPanel 同机制） */}
      {isScript && scriptDone && selected && !dragging && !isMultiSelected && (
        <InverseScaleBox
          minZoom={0.5}
          fixedContent
          fitViewport
          className="absolute left-1/2 z-30"
          style={{ bottom: '100%', paddingBottom: '36px', transform: 'translateX(-50%)', transformOrigin: 'center bottom' }}
          scaleOrigin="center bottom"
        >
          <ScriptNodeToolbar
            data={data}
            isWorking={isWorking}
            onRegenerate={() => onNodeAction(data.id)}
            onOpenPanel={() => onOpenScript?.(data.id)}
            onSend={() => {}}
            onUpdateModel={(modelId) => onNodeUpdate(data.id, { model: modelId })}
            onBatchStoryboard={(selected, options) =>
              generateBatchStoryboard({ scriptNodeId: data.id, onNodeAction, onAddNode, shotIds: selected.map((s) => s.id), imageOptions: options })}
            onBatchVideos={(selectedNodeIds, options) =>
              generateBatchVideos({ scriptNodeId: data.id, onNodeAction, onAddNode, nodeIds: selectedNodeIds, videoOptions: options })}
          />
        </InverseScaleBox>
      )}

      {/* 媒体尺寸显示 — 仅在媒体上传节点/图片节点/视频节点显示，位于节点卡片右上角，与标题水平中心对齐 */}
      {(() => {
        // 图片节点：有 image/images 且记录了 imageDimensions
        // 视频节点：有 videoUri/videoUris 且 videoMetadata 含 width/height
        const imageDims = data.data.imageDimensions;
        const videoMeta = data.data.videoMetadata;
        if (mediaType === 'image' && imageDims && imageDims.width && imageDims.height) {
          return <MediaDimensions width={imageDims.width} height={imageDims.height} />;
        }
        if (mediaType === 'video' && videoMeta && videoMeta.width && videoMeta.height) {
          return <MediaDimensions width={videoMeta.width} height={videoMeta.height} />;
        }
        return null;
      })()}

      <div
        data-node-card={data.id}
        className={`group relative overflow-hidden transition-[border-color,box-shadow,background-color] duration-300 ${isSelfWrite || isDirector || isLayerEdit ? 'rounded-[32px]' : 'rounded-[16px]'} border ${
          (isWorking || isQueued) && !isSelfWrite ? 'charge-border' :
          selected && !isMultiSelected
            ? 'ring-2 ring-white/50 shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] border-white/40'
            : isMultiSelected
              ? 'border-transparent'
              : 'border-white/25 hover:border-white/40'
        } ${data.type === NodeType.PANORAMA_GENERATOR && data.data.isPanoramaPreviewOpen && !isCtrlHeld ? 'nodrag nowheel' : ''} ${isSelfWrite && selfWriteResult.editMode === 'edit' ? 'nodrag nowheel' : ''}`}
        style={{
          width: nodeWidth,
          height: cardHeight,
          // backdrop-filter: blur(24px) 强制浏览器为每个节点创建独立 GPU 合成层
          // ReactFlow 的 inline transform: translate(Xpx,Ypx) 会覆盖 CSS 的 translateZ(0)，
          // 所以 translateZ(0) 不生效。backdrop-filter 是唯一能强制创建合成层的机制。
          // 有合成层时平移=GPU移动纹理(极廉价)，无合成层时平移=CPU重绘(节点多时卡顿)
          background: isSelfWrite ? '#262626' : (selected ? 'rgba(38, 38, 38, 0.95)' : 'rgba(38, 38, 38, 0.85)'),
          backdropFilter: isSelfWrite ? 'none' : 'blur(24px)',
          // contain: 隔离节点的布局/样式，避免一个节点变化触发其他节点重新计算布局
          // 注意：不能用 contain: paint — backdrop-filter 需要采样元素后面的背景
          // contain: paint 会创建隔离的绘制层，backdrop-filter 无法正确采样 → 闪烁
          contain: 'layout style',
        }}
      >
        {hasMedia && !isSelfWrite && !isDirector && !isLayerEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              useReferenceStore.getState().addReference(data);
              toast.success('已引用为参考');
              onOpenChat?.();
            }}
            className={`absolute top-2 right-2 z-[100] flex items-center justify-center w-7 h-7 rounded-lg bg-black/60 backdrop-blur-sm border border-white/20 text-white/80 hover:text-white hover:bg-white/20 transition-all ${isHovered || selected ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            title="引用为参考"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
        )}
        <div className="w-full h-full relative">
          {renderCardContent()}
        </div>

        {(isWorking || isQueued || chargeComplete) && !isSelfWrite && (
          <div className="charge-overlay">
            {isWorking && <div className="charge-scan-line" />}
            {chargeComplete && <div className="charge-complete-flash" />}
            {isQueued && (
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-[10px] leading-4 backdrop-blur-sm whitespace-nowrap">
                {data.data.progress || '排队中'}
              </div>
            )}
          </div>
        )}

        {/* v1.4.77 脚本节点手动连线后隐藏「尝试」入口：点入口路径靠写入 script 骨架（scriptData）
            使快捷键消失，但手动拖线连上游（剧本/角色图）不产生 scriptData，「尝试」引导菜单仍残留；
            「尝试」的职能是引导创建上游内容，节点已有任意上游输入时即应切换为无快捷键的生成器空卡。
            inputAssets 只收图/视频/音频，剧本为文本节点不计入，故脚本节点改按 inputs 判定 */}
        {!hasMedia && !uploadNode && inputAssets.length === 0 && !isSelfWrite && !isDirector && !isLayerEdit && !(isScript && (scriptDone || isWorking || !!scriptData || data.inputs.length > 0)) && (
          <TryActions
            nodeId={data.id}
            nodeType={data.type}
            onTryAction={isSmartEdit
              // 智能剪辑四模板入口：点击即携带模式打开全屏面板（§4.1.1）
              ? (actionId: string) => onOpenSmartEdit?.(data.id, { mode: actionId as import('@/types/smartEdit').EditMode })
              : handleTryAction}
          />
        )}
      </div>

      {selected && !uploadNode && !data.data.isManualUpload && !dragging && !isMultiSelected && !activeFeature && !gridSplit && !showPanoramaPanel && !showMultiAnglePanel && !showLightingPanel && !isSelfWrite && !isDirector && !isLayerEdit && !isScript && !isSmartEdit && (
        <InverseScaleBox
          fixedContent
          fitViewport
          className="absolute left-1/2 z-20"
          style={{
            top: cardHeight + 30,
            width: data.type === NodeType.IMAGE_GENERATOR && currentImageMode === 'UPSCALE' ? 420 : 660,
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
            pointerEvents: 'auto',
            transition: 'opacity 0.5s cubic-bezier(0.32, 0.72, 0, 1), top 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
            transformOrigin: 'top center',
          }}
          scaleOrigin="top center"
        >
          <EditorPanel
            nodeId={data.id}
            nodeType={data.type}
            status={data.status}
            prompt={localPrompt}
            model={data.data.model}
            aspectRatio={data.data.aspectRatio}
            resolution={data.data.resolution}
            duration={data.data.duration}
            imageCount={data.data.imageCount}
            videoCount={data.data.videoCount}
            upscaleStrength={data.data.upscaleStrength}
            generateAudio={data.data.generateAudio}
            inputAssets={inputAssets}
            isOpen={isOpen}
            currentImageMode={currentImageMode}
            onPromptChange={handlePromptChange}
            onPromptSubmit={handlePromptSubmit}
            onUpdate={onNodeUpdate}
            onGenerate={handleGenerate}
            onImageModeSwitch={handleImageModeSwitch}
            onAddUploadNode={data.type === NodeType.PANORAMA_GENERATOR ? handleAddUploadNodeForPanorama : undefined}
          />
        </InverseScaleBox>
      )}

      {isScript && selected && !dragging && !isMultiSelected && (
        <InverseScaleBox
          fixedContent
          fitViewport
          className="absolute left-1/2 z-20"
          style={{
            top: cardHeight + 30,
            width: 720,
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
            pointerEvents: 'auto',
            transition: 'opacity 0.5s cubic-bezier(0.32, 0.72, 0, 1), top 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
            transformOrigin: 'top center',
          }}
          scaleOrigin="top center"
        >
          <ScriptQuickInput
            data={data}
            isWorking={isWorking}
            draftText={scriptQuickText}
            onDraftChange={setScriptQuickText}
            onRegenerate={() => {}}
            onOpenPanel={() => {}}
            onSend={(text) => onNodeAction(data.id, text)}
            onUpdateModel={(modelId) => onNodeUpdate(data.id, { model: modelId })}
          />
        </InverseScaleBox>
      )}

      {/* v0.32：智能剪辑底部参数面板（镜像脚本节点先例）——点击选中节点即显示，取消选中/拖拽/多选时隐藏 */}
      {isSmartEdit && selected && !dragging && !isMultiSelected && (
        <InverseScaleBox
          fixedContent
          fitViewport
          className="absolute left-1/2 z-20"
          style={{
            top: cardHeight + 30,
            width: 720,
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
            pointerEvents: 'auto',
            transition: 'opacity 0.5s cubic-bezier(0.32, 0.72, 0, 1), top 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
            transformOrigin: 'top center',
          }}
          scaleOrigin="top center"
        >
          <SmartEditQuickInput
            data={data}
            upstreamVideo={upstreamVideo}
            draftText={smartQuickText}
            onDraftChange={setSmartQuickText}
            onSend={(session) => onOpenSmartEdit?.(data.id, session)}
          />
        </InverseScaleBox>
      )}

      {activeFeature && (
        <FeatureConfirmBar
          data={data}
          cardHeight={cardHeight}
          activeFeature={activeFeature}
          onNodeUpdate={onNodeUpdate}
          onNodeAction={onNodeAction}
          onAddNode={onAddNode}
          onClose={handleFeatureClose}
        />
      )}

      <MagneticHandle
        type="target"
        position={Position.Left}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={nodeWidth}
      />

      <MagneticHandle
        type="source"
        position={Position.Right}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={nodeWidth}
      />
    </div>
  );
});

export const GenerateNode = memo(GenerateNodeComponent, (prevProps, nextProps) => {
  const pd = prevProps.data?.data;
  const nd = nextProps.data?.data;
  return (
    prevProps.data?.id === nextProps.data?.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.dragging === nextProps.dragging &&
    prevProps.data?.status === nextProps.data?.status &&
    prevProps.data?.width === nextProps.data?.width &&
    prevProps.data?.height === nextProps.data?.height &&
    pd?.model === nd?.model &&
    pd?.image === nd?.image &&
    pd?.videoUri === nd?.videoUri &&
    pd?.audioUri === nd?.audioUri &&
    pd?.error === nd?.error &&
    pd?.progress === nd?.progress &&
    pd?.prompt === nd?.prompt &&
    pd?.generationMode === nd?.generationMode &&
    pd?.panoramaOutputUrl === nd?.panoramaOutputUrl &&
    pd?.isPanoramaPreviewOpen === nd?.isPanoramaPreviewOpen &&
    pd?.images === nd?.images &&
    pd?.videoUris === nd?.videoUris &&
    pd?.aspectRatio === nd?.aspectRatio &&
    pd?.resolution === nd?.resolution &&
    pd?.duration === nd?.duration &&
    pd?.imageCount === nd?.imageCount &&
    pd?.videoCount === nd?.videoCount &&
    pd?.upscaleStrength === nd?.upscaleStrength &&
    pd?.generateAudio === nd?.generateAudio &&
    pd?.isManualUpload === nd?.isManualUpload &&
    pd?.subMode === nd?.subMode &&
    pd?.editMode === nd?.editMode &&
    pd?.nodeSize === nd?.nodeSize &&
    pd?.richContent === nd?.richContent &&
    pd?.textContent === nd?.textContent &&
    pd?.imageDimensions === nd?.imageDimensions &&
    pd?.videoMetadata === nd?.videoMetadata &&
    pd?.multiAngleParams === nd?.multiAngleParams &&
    pd?.lightingParams === nd?.lightingParams &&
    (pd as any)?.layerDocument === (nd as any)?.layerDocument &&
    (pd as any)?.thumbnailDataUrl === (nd as any)?.thumbnailDataUrl &&
    // 脚本节点：script 整包引用比较（updateScript/updateNode 均整包替换，引用变化即重渲染）
    (pd as any)?.script === (nd as any)?.script &&
    prevProps.data?.inputs?.length === nextProps.data?.inputs?.length
  );
});
